import { computeLayerCache, type LayerCacheEntry } from "./buildCache";
import { prepareBuildFiles, type BuildFile } from "./buildFiles";
import { ExpectedError } from "./expectedError";
import { isStepTestPath } from "./stepConfig";

// Version of the derivation stored on commit_step_layers rows. Bump whenever
// the derivation changes — BUILD_CACHE_NAMESPACE, Dockerfile parsing,
// prepareBuildFiles, or the hashing layout — so readers ignore rows computed
// by an older algorithm and fall back to deriving from files.
export const STEP_LAYERS_VERSION = 1;

/** One build layer of a commit step, stored on commit_step_layers. */
export type StepLayer = { hash: string; cacheOutputs?: string[] };

/**
 * Shape a step's raw commit files (full paths, with content) into the code
 * files `computeStepLayers` expects: test files dropped, paths relative to the
 * step directory. Shared by both commit-time writers so they cannot diverge.
 */
export function stepCodeFiles(
  stepDir: string,
  files: Array<{ path: string; content: string }>
): BuildFile[] {
  return files
    .filter((f) => !isStepTestPath(f.path))
    .map((f) => ({
      path: f.path.substring(stepDir.length + 1),
      content: f.content,
    }));
}

export type StepLayerScope = {
  spaceId: string;
  workflowId: string;
  stepDir: string;
};

/**
 * Derive a step's build layers from its files, unscoped, for persistence at
 * commit time. Must match what `computeLayerCache` derives at enqueue and
 * hydration time: same file preparation, same hashing, no scope (the layer
 * chain is scope-free; scope is a key prefix readers add).
 *
 * Returns null for an unparseable Dockerfile: committing one is allowed (the
 * teaching error surfaces at enqueue time), so no facet row is written and
 * readers fall back to deriving from files — which raises that same error.
 */
export async function computeStepLayers(
  codeFiles: BuildFile[]
): Promise<StepLayer[] | null> {
  let cache: LayerCacheEntry[];
  try {
    cache = await computeLayerCache(prepareBuildFiles(codeFiles));
  } catch (error) {
    if (error instanceof ExpectedError) return null;
    throw error;
  }
  return cache.map((entry) =>
    entry.cacheOutputs?.length
      ? { hash: entry.cacheKey, cacheOutputs: entry.cacheOutputs }
      : { hash: entry.cacheKey }
  );
}

/**
 * Rebuild the scoped layer-cache entries `computeLayerCache(files, [], scope)`
 * would produce, from stored layers instead of files.
 */
export function scopedLayerCache(
  layers: StepLayer[],
  scope: StepLayerScope
): LayerCacheEntry[] {
  return layers.map((layer) => ({
    cacheKey: `${scope.spaceId}:${scope.workflowId}:${scope.stepDir}:${layer.hash}`,
    layerHash: "",
    ...(layer.cacheOutputs?.length ? { cacheOutputs: layer.cacheOutputs } : {}),
  }));
}
